import { AbstractShape } from "../abstract/Shape";

export const MINIMUM_LEAF_SIZE = 100;

/**
 * Scales a shape and all of its descendants from the root's top-left corner.
 *
 * Positive values are regular scale factors. A negative value is a convenient
 * shrink shorthand: `-2` means divide all dimensions by two. A zero factor is
 * invalid because it would create a zero-sized resize box.
 */
export function scaleSubtreeFromTopLeft(
  root: AbstractShape,
  requestedScale: number,
  minimumLeafSize: number = MINIMUM_LEAF_SIZE
): void {
  if (!Number.isFinite(requestedScale) || !Number.isFinite(minimumLeafSize)) {
    throw new Error(`Invalid scale values: scale=${requestedScale}, minimum=${minimumLeafSize}`);
  }
  if (requestedScale === 0 || minimumLeafSize <= 0) {
    throw new Error(`Scale and minimum size must be greater than zero`);
  }

  const scale = requestedScale < 0 ? 1 / Math.abs(requestedScale) : requestedScale;
  if (scale === 1) return;

  const shapes: AbstractShape[] = [];
  const leaves: AbstractShape[] = [];
  const stack: AbstractShape[] = [root];

  while (stack.length) {
    const shape = stack.pop()!;
    shapes.push(shape);

    if (shape.children?.length) {
      stack.push(...shape.children);
    } else {
      leaves.push(shape);
    }
  }

  // Keep the subtree proportional. If any deepest child would become smaller
  // than 100x100, stop the whole scale operation at that child's limit.
  const minimumScale = leaves.reduce((requiredScale, leaf) => {
    if (leaf.width <= 0 || leaf.height <= 0) return requiredScale;
    return Math.max(
      requiredScale,
      minimumLeafSize / leaf.width,
      minimumLeafSize / leaf.height
    );
  }, 0);
  const effectiveScale = scale < 1 ? Math.max(scale, minimumScale) : scale;

  for (const shape of shapes) {
    if (shape !== root) {
      shape.x *= effectiveScale;
      shape.y *= effectiveScale;
    }
    shape.setSize(shape.width * effectiveScale, shape.height * effectiveScale);
  }
}
